import { useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Loader2,
  SendHorizonal,
} from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import { cn } from "../../../lib/utils";
import { useBreakpoint } from "../../../hooks/useIsMobile";
import { Button } from "../../ui/button";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "../../ui/drawer";

type RequirementSubmitMode = "review" | "continue";

interface MobileNavBarProps {
  onPrevious: () => void;
  onNext: () => void;
  hasPrevious: boolean;
  hasNext: boolean;
  currentStep: number;
  totalSteps: number;
  onOpenStepSheet: () => void;
  isSubmittable?: boolean;
  submitMode?: RequirementSubmitMode;
  isSubmitting?: boolean;
  isForwardDisabled?: boolean;
  isHidden?: boolean;
  nextTabName?: string;
  onNextTab?: () => void;
}

export function MobileNavBar({
  onPrevious,
  onNext,
  hasPrevious,
  hasNext,
  currentStep,
  totalSteps,
  onOpenStepSheet,
  isSubmittable = false,
  submitMode = "review",
  isSubmitting = false,
  isForwardDisabled = false,
  isHidden = false,
  nextTabName,
  onNextTab,
}: MobileNavBarProps) {
  const { isDesktop } = useBreakpoint();
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  // Only render on mobile/tablet
  if (isDesktop) return null;

  // Determine forward button behavior
  const showCrossTabButton = !hasNext && !isSubmittable && !!nextTabName;
  const isReviewSubmit = isSubmittable && submitMode === "review";
  const isContinueSubmit = isSubmittable && submitMode === "continue";
  const forwardAction = isReviewSubmit
    ? () => setIsConfirmOpen(true)
    : showCrossTabButton
      ? onNextTab
      : onNext;
  const forwardLabel = isReviewSubmit
    ? "Enviar"
    : isContinueSubmit
      ? "Continuar"
      : showCrossTabButton
        ? "Siguiente tab"
        : "Siguiente";
  const showForward = hasNext || isSubmittable || showCrossTabButton;

  return (
    <>
      <AnimatePresence>
        {!isHidden && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ type: "spring", stiffness: 400, damping: 35 }}
            className={cn(
              "fixed bottom-0 left-0 right-0 z-40",
              "backdrop-blur-md bg-white/90 border-t border-slate-100",
            )}
            style={{
              height:
                "calc(var(--mobile-bottom-bar-height) + var(--mobile-safe-area-bottom))",
              paddingBottom: "var(--mobile-safe-area-bottom)",
            }}
          >
            <div className="flex items-center justify-between h-[var(--mobile-bottom-bar-height)] px-4">
              {/* Previous button */}
              <Button
                variant="ghost"
                onClick={onPrevious}
                disabled={!hasPrevious || isSubmitting}
                className={cn(
                  "gap-1 text-slate-700 min-w-[80px]",
                  !hasPrevious && "invisible",
                )}
              >
                <ChevronLeft className="w-4 h-4" />
                Anterior
              </Button>

              {/* Step indicator — tappable */}
              <button
                onClick={onOpenStepSheet}
                className="flex flex-col items-center justify-center gap-0.5 px-4 py-2 rounded-lg hover:bg-slate-50 active:bg-slate-100 transition-colors min-w-[100px]"
              >
                <span className="text-sm font-semibold text-slate-900">
                  Paso {currentStep}/{totalSteps}
                </span>
                <span className="text-[10px] text-slate-400 leading-none">
                  Ver todos
                </span>
              </button>

              {/* Next / Submit button */}
              <Button
                onClick={forwardAction}
                disabled={isSubmitting || isForwardDisabled}
                className={cn(
                  "gap-1 bg-slate-900 hover:bg-slate-800 text-white min-w-[80px]",
                  !showForward && "invisible",
                )}
              >
                {isSubmitting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : isReviewSubmit ? (
                  <SendHorizonal className="h-4 w-4" />
                ) : null}
                {forwardLabel}
                {!isReviewSubmit && !showCrossTabButton && (
                  <ChevronRight className="w-4 h-4" />
                )}
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Confirmation drawer for submittable requirements */}
      <Drawer
        open={isReviewSubmit && isConfirmOpen}
        onOpenChange={setIsConfirmOpen}
      >
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle>Confirmar envío</DrawerTitle>
            <DrawerDescription>
              ¿Estás seguro de enviar el requisito a revisión? Una vez enviado,
              no podrás modificar los datos hasta que sea revisado.
            </DrawerDescription>
          </DrawerHeader>
          <div className="flex flex-col gap-3 px-4 pb-6">
            <Button
              className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white text-base"
              onClick={() => {
                setIsConfirmOpen(false);
                onNext();
              }}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Enviando..." : "Confirmar"}
            </Button>
            <Button
              variant="outline"
              className="w-full h-12 text-base"
              onClick={() => setIsConfirmOpen(false)}
              disabled={isSubmitting}
            >
              Cancelar
            </Button>
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
}
